import { useState, useCallback, useEffect, useRef, RefObject } from 'react'
import { FilterSection, SelectedFilterValues } from './FilterPopup.types'

interface UseFilterPopupOptions {
    sections: FilterSection[]
    defaultOpen?: boolean
    onApply?: (selectedValues: SelectedFilterValues) => void
    onCancel?: () => void
    activatorRef?: RefObject<HTMLElement>
    isOpen: boolean
    onToggle: () => void
}

export const useFilterPopup = ({
    sections,
    defaultOpen = false,
    onApply,
    onCancel,
    activatorRef,
    isOpen,
    onToggle,
}: UseFilterPopupOptions) => {
    const [collapsedSections, setCollapsedSections] = useState<
        Record<string, boolean>
    >({})
    const [searchValues, setSearchValues] = useState<Record<string, string>>({})
    const [selectedOptions, setSelectedOptions] = useState<
        Record<string, string[]>
    >({})

    const popupRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const initialOptions = sections.reduce(
            (acc, section) => {
                acc[section.id] = [...section.selectedOptionIds]
                return acc
            },
            {} as Record<string, string[]>
        )
        setSelectedOptions(initialOptions)
    }, [
        sections,
        sections.map((section) => section.selectedOptionIds).join(','),
    ])

    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node
            const isInsideActivator = activatorRef?.current?.contains(target)
            const isInsidePopup = popupRef.current?.contains(target)

            if (!isInsideActivator && !isInsidePopup) {
                onToggle()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [isOpen, activatorRef, onToggle])

    const toggleCollapse = useCallback((sectionId: string) => {
        setCollapsedSections((prev) => ({
            ...prev,
            [sectionId]: !prev[sectionId],
        }))
    }, [])

    const updateSearch = useCallback((sectionId: string, value: string) => {
        setSearchValues((prev) => ({ ...prev, [sectionId]: value }))
    }, [])

    const toggleOption = useCallback((sectionId: string, optionId: string) => {
        setSelectedOptions((prev) => {
            const current = prev[sectionId] ?? []
            const isSelected = current.includes(optionId)

            return {
                ...prev,
                [sectionId]: isSelected
                    ? current.filter((id) => id !== optionId)
                    : [...current, optionId],
            }
        })
    }, [])

    const getFilteredOptions = useCallback(
        (section: FilterSection) => {
            const search = searchValues[section.id]?.toLowerCase() ?? ''
            return search
                ? section.options.filter((opt) =>
                      opt.label.toLowerCase().includes(search)
                  )
                : section.options
        },
        [searchValues]
    )

    const getSelectedForSection = useCallback(
        (sectionId: string) => {
            return selectedOptions[sectionId] ?? []
        },
        [selectedOptions]
    )

    const resetState = useCallback(() => {
        const initialOptions = sections.reduce(
            (acc, section) => {
                acc[section.id] = []
                return acc
            },
            {} as Record<string, string[]>
        )
        setSelectedOptions(initialOptions)
        setSearchValues({})
    }, [sections])

    const handleApply = useCallback(() => {
        const selectedValues: SelectedFilterValues = sections.reduce(
            (acc, section) => {
                const sectionSelected = selectedOptions[section.id] ?? []
                acc[section.id] = {
                    sectionLabel: section.label,
                    selectedOptions: sectionSelected.map((optionId) => {
                        const option = section.options.find(
                            (opt) => opt.id === optionId
                        )
                        return {
                            id: optionId,
                            label: option?.label ?? optionId,
                        }
                    }),
                }
                return acc
            },
            {} as SelectedFilterValues
        )

        onApply?.(selectedValues)
        onToggle()
    }, [sections, selectedOptions, onApply, onToggle])

    const handleCancel = useCallback(() => {
        resetState()
        onCancel?.()
        onToggle()
    }, [resetState, onCancel, onToggle])

    return {
        collapsedSections,
        searchValues,
        selectedOptions,
        popupRef,
        toggleCollapse,
        updateSearch,
        toggleOption,
        getFilteredOptions,
        getSelectedForSection,
        handleApply,
        handleCancel,
    }
}
